import {
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  deleteUser,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  revokeAccessToken,
} from 'firebase/auth';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Capacitor } from '@capacitor/core';
import { api } from '../../lib/api';
import { auth } from '../../lib/firebase';

/**
 * Delete account (PRD 13): re-authenticate first (Firebase requires a recent sign-in), then remove the
 * account from D1, then delete the Firebase user and, for Apple, revoke the Apple token.
 */
export async function deleteAccount(opts: { typed: string; email: string | null; password?: string }) {
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');
  const providerId = user.providerData[0]?.providerId ?? 'password';
  let appleAuthCode: string | undefined;

  if (providerId === 'password') {
    if (!opts.password) throw Object.assign(new Error('Password required'), { code: 'needs-password' });
    await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email ?? '', opts.password));
  } else if (providerId === 'apple.com') {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    const result = await reauthenticateWithPopup(user, provider);
    appleAuthCode = OAuthProvider.credentialFromResult(result)?.accessToken;
  } else if (Capacitor.isNativePlatform()) {
    // Popups don't work in the app's WebView; re-authenticate through the native Google sheet instead.
    const idToken = (await FirebaseAuthentication.signInWithGoogle()).credential?.idToken;
    if (!idToken) throw new Error('Google sign-in returned no ID token');
    await reauthenticateWithCredential(user, GoogleAuthProvider.credential(idToken));
  } else {
    await reauthenticateWithPopup(user, new GoogleAuthProvider());
  }

  await api('DELETE', '/me', { confirmation: opts.typed });
  if (appleAuthCode) await revokeAccessToken(auth, appleAuthCode).catch(() => {});
  await deleteUser(user);
  if (Capacitor.isNativePlatform()) await FirebaseAuthentication.signOut().catch(() => {});
}
